import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';

import { IcGPS } from '@/assets/images';
import { IViewItem } from '@/components';
import { IMasterNamePerson, IEmergencyContactRelationship } from '@/models';
import { getAddressLocation } from '@/services';
import { colors } from '@/theme/variables';
import { formatPhoneNumber } from '@/utils';

const headerCellStyle = {
  color: colors.grey[70],
  fontWeight: 500,
  fontSize: '14px',
};

const bodyCellStyle = {
  fontWeight: 500,
  color: colors.grey[80],
};

interface EmergencyContactEntry {
  emergencyContact: IMasterNamePerson;
  relationship?: IEmergencyContactRelationship | null;
}

interface EmergencyContactsTableProps {
  emergencyContacts: EmergencyContactEntry[];
  items: IViewItem[];
  showMapsIcon?: boolean;
}

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

export const EmergencyContactsTable = ({
  emergencyContacts,
  items,
  showMapsIcon = false,
}: EmergencyContactsTableProps) => {
  if (!emergencyContacts.length) {
    return (
      <Box sx={{ padding: 1, paddingBottom: 2, fontSize: 12, marginLeft: 2 }}>
        No emergency contacts available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        paddingX: '20px',
        paddingBottom: 2,
      }}
    >
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            {items.map((item) => (
              <TableCell key={item.value as string} sx={headerCellStyle}>
                {item.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {emergencyContacts.map((contact, index) => {
            const addressesArray = contact.emergencyContact?.addresses;
            const singleAddress = contact.emergencyContact?.address;

            let addressText = '';
            if (addressesArray && addressesArray.length > 0) {
              addressText = getAddressLocation(addressesArray[0]);
            } else if (singleAddress) {
              const addressLocation = getAddressLocation(singleAddress);
              addressText = addressLocation ? `${addressLocation}` : '';
            }

            return (
              <TableRow key={`emergency-contact-${index}`}>
                <TableCell sx={bodyCellStyle}>
                  {contact.emergencyContact?.lastName || '—'}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {contact.emergencyContact?.firstName || '—'}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {contact.emergencyContact?.middleName || '—'}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {contact.relationship?.relationship || '—'}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {Array.isArray(contact.emergencyContact?.phoneNumbers) &&
                  contact.emergencyContact.phoneNumbers.length > 1 ? (
                    <Tooltip
                      title={
                        <Box>
                          {contact.emergencyContact.phoneNumbers
                            .slice(1)
                            .map((phone, i) => (
                              <div key={i}>
                                {phone?.type}:{' '}
                                {formatPhoneNumber(phone?.phoneNumber)}
                              </div>
                            ))}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <span>
                          {contact.emergencyContact.phoneNumbers[0]?.type}:{' '}
                          {formatPhoneNumber(
                            contact.emergencyContact.phoneNumbers[0]
                              ?.phoneNumber,
                          )}
                        </span>
                        <Chip
                          label={`+${
                            contact.emergencyContact.phoneNumbers.length - 1
                          }`}
                          size="small"
                        />
                      </Box>
                    </Tooltip>
                  ) : Array.isArray(contact.emergencyContact?.phoneNumbers) &&
                    contact.emergencyContact.phoneNumbers.length === 1 ? (
                    <span>
                      {contact.emergencyContact.phoneNumbers[0]?.type}:{' '}
                      {formatPhoneNumber(
                        contact.emergencyContact.phoneNumbers[0]?.phoneNumber,
                      )}
                    </span>
                  ) : (
                    <span>—</span>
                  )}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {Array.isArray(contact.emergencyContact?.emails) &&
                  contact.emergencyContact.emails.length > 1 ? (
                    <Tooltip
                      title={
                        <Box>
                          {contact.emergencyContact.emails
                            .slice(1)
                            .map((email, i) => (
                              <div key={i}>{email}</div>
                            ))}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <span>{contact.emergencyContact.emails[0]}</span>
                        <Chip
                          label={`+${
                            contact.emergencyContact.emails.length - 1
                          }`}
                          size="small"
                        />
                      </Box>
                    </Tooltip>
                  ) : Array.isArray(contact.emergencyContact?.emails) &&
                    contact.emergencyContact.emails.length === 1 ? (
                    contact.emergencyContact.emails[0]
                  ) : (
                    <span>—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{addressText}</span>
                    {showMapsIcon && addressText && (
                      <Tooltip title="Open in Google Maps" placement="top">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openInGoogleMaps(addressText);
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            border: '1px solid #CFD6DA',
                            backgroundColor: '#FFFFFF',
                            color: '#3E5A6A',

                            transition: 'all 0.2s ease',
                            pointerEvents: 'auto',
                          }}
                        >
                          <IcGPS />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {Array.isArray(contact.emergencyContact?.addresses) &&
                  contact.emergencyContact.addresses.length
                    ? contact.emergencyContact.addresses[0]?.subAddress || '—'
                    : '—'}
                </TableCell>
                <TableCell sx={bodyCellStyle}>
                  {contact.relationship?.isPrimary ? 'Yes' : 'No'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
